import { Component, OnInit, ViewChild } from '@angular/core';
import { Chart, ChartOptions, ChartType, ChartTypeRegistry, registerables } from 'chart.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { UtilityService } from '../services/utility.service';
import { UserService } from '../services/user.service';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { EnrollmentService } from '../services/enrollment.service';
import { WorkorderService } from '../services/workorder.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-chart-dashboard',
  templateUrl: './chart-dashboard.component.html',
  styleUrls: ['./chart-dashboard.component.scss']
})
export class ChartDashboardComponent implements OnInit {
  public displayedLabels: string[] = [];
  public chartData: any = [];
  public chartType = ['bar', 'doughnut', 'line', 'pie', 'polarArea', 'radar'];
  public selectedChartType: keyof ChartTypeRegistry = 'bar';
  public selectedWorkorderType: string;
  public chart: Chart;
  dataMatrix: any = [];
  public barChartData: any = [];
  public barChartType: ChartType = 'bar';

  ///-----------Workorder Grid-------
  displayedColumns: string[] = ['WorkorderID', 'WorkorderType', 'AccountHolder', 'Distributor', 'Contractor', 'WorkorderStatus', 'Updated', 'Details'];
  dataSource = new MatTableDataSource<any>();
  workorderIDFilter = new FormControl();
  workorderTypeFilter = new FormControl();
  accountHolderFilter = new FormControl();
  distributorFilter = new FormControl();
  contractorFilter = new FormControl();
  workorderStatusFilter = new FormControl();
  globalFilter = '';
  fromDate = new Date(new Date().getFullYear(), 0, 1); // January 1st of current year
  toDate = new Date(); // Current date
  programId = 'ALL';

  filteredValues = {
    WorkorderID: '', WorkorderType: '', AccountHolder: '',
    Distributor: '', Contractor: '', WorkorderStatus: ''
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  constructor(public utilityService: UtilityService,
    private spinner: NgxSpinnerService,
    public userService: UserService,
    private workorderService: WorkorderService,
    private enrollmentService: EnrollmentService,
    private router: Router) {
    Chart.register(...registerables);
  }

  ngOnInit(): void {
    this.getbuckets();
    this.getMatrix();
    this.getWorkOrders();
    this.initializeColumnFilters();
    this.dataSource.filterPredicate = this.customFilterPredicate();
  }

  initializeColumnFilters() {
    const filterMap = {
      WorkorderID: this.workorderIDFilter,
      WorkorderType: this.workorderTypeFilter,
      AccountHolder: this.accountHolderFilter,
      Distributor: this.distributorFilter,
      Contractor: this.contractorFilter,
      WorkorderStatus: this.workorderStatusFilter
    };

    Object.keys(filterMap).forEach((key) => {
      filterMap[key].valueChanges.subscribe(value => {
        this.filteredValues[key] = value || '';
        this.dataSource.filter = JSON.stringify(this.filteredValues);
      })
    })
  }

  onDateChanges(event: { fromDate: Date | null, toDate: Date | null }) {
    this.fromDate = event.fromDate;
    this.toDate = event.toDate;
    if (this.fromDate && this.toDate && this.fromDate <= this.toDate) {
      this.getbuckets();
      this.getMatrix();
      this.getWorkOrders();
    }
  }

  defaultDatePicker() {
    return {
      officeId: this.utilityService.currentUtility.OfficeId,
      fromDate: formatDate(this.fromDate, 'yyyy-MM-dd', 'en-US'),
      toDate: formatDate(this.toDate, 'yyyy-MM-dd', 'en-US'),
      programId: this.programId
    }
  }

  updateDataSource(data: any[], filteredValues: any) {
    data.forEach(element => {
      for (const key in element) {
        if (!element[key] || element[key] === null) {
          element[key] = '';
        }
      }
    });
    this.dataSource.filter = JSON.stringify(filteredValues);
  }

  getbuckets() {
    var temp = new Array<any>();
    this.displayedLabels = [];
    this.barChartData = [];
    this.selectedWorkorderType = '';
    const defaultDates = this.defaultDatePicker();
    this.userService.getDashboardBuckets(defaultDates.fromDate, defaultDates.toDate, this.programId).subscribe((data) => {
      data["Buckets"].forEach(element => {
        if (element.label != 'Total Workorders') {
          this.userService.resourceRole?.toLowerCase() == 'admin' ? this.displayedLabels.push(element.WorkorderStatus) : "";
          temp.push(element.Count);
        }
      });
      this.chartData = [
        {
          data: temp,
          label: 'Workorder Count/ s',
          categoryPercentage: 1,
        }
      ];
    });
  }

  changeSelectedType(event) {
    this.selectedChartType = event.value;
  }

  ///---------------------Workorder Type and Chart Type-------------------
  getMatrix() {
    this.spinner.show();
    this.userService.getAdminMatrix(this.defaultDatePicker()).subscribe((data1) => {
      this.dataMatrix = data1["DATA"];
      if (this.userService.resourceRole?.toLowerCase() != 'admin') {
        this.processData(this.dataMatrix);
      }
      this.spinner.hide();
    });
  }

  changeWorkorderType(event) {
    this.selectedWorkorderType = event.value;
    this.getWorkorderTypeChart();
  }

  public barChartOptions: any = {
    scaleShowVerticalLines: false,
    responsive: true,
    scales: {
      xAxes: [{
        gridLines: {
          offsetGridLines: true
        }
      }],
      yAxes: [{
        ticks: {
          min: 5
        }
      }]
    }
  };

  scrollToBuckets(bucketsSection: HTMLElement) {
    if (bucketsSection) {
      bucketsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  changeWoSelectedType(event) {
    this.barChartType = event.value;
    this.getWorkorderTypeChart();
  }

  getWorkorderTypeChart() {
    const temp = this.dataMatrix.filter((item) => item.WorkorderType === this.selectedWorkorderType);
    const labels = Object.keys(temp[0]).filter(key => key !== 'WorkorderType');
    const data = labels.map(label => temp[0][label] || 0);
    this.barChartData = [
      {
        data: data,
        label: temp[0]?.["WorkorderType"],
        categoryPercentage: 1,
      }];
  }

  public chartClicked(e: any, isWorkorderType: boolean, workordersSection: HTMLElement) {
    if (isWorkorderType) {
      let filterValue = {
        WorkorderType: this.selectedWorkorderType
      }
      this.applyFilter(filterValue, this.displayedLabels[e.active[0]?.index]);
    }
    else
      this.applyFilter(this.displayedLabels[e.active[0]?.index]);
    this.scrollToBuckets(workordersSection);
  }

  public matrixChartClicked(e: any, workordersSection: HTMLElement) {
    let WorkorderType;
    for (let index = 0; index < this.barChartData.length; index++) {
      index == e.active[0]?.datasetIndex ? WorkorderType = this.barChartData[index]["label"] : "";
    }
    let filterValue = {
      WorkorderType: WorkorderType,
      WorkorderStatus: this.displayedLabels[e.active[0].index],
    }
    this.applyFilter(filterValue, this.displayedLabels[e.active[0]?.index]);
    this.scrollToBuckets(workordersSection);
  }

  //---------Workorder grid----------
  customFilterPredicate() {
    return (data: any, filter: string): boolean => {
      const search = JSON.parse(filter);

      if (this.globalFilter) {
        return this.matchExact(data.WorkorderStatus, this.globalFilter);
      }

      const columns = [
        'WorkorderID',
        'WorkorderType',
        'AccountHolder',
        'Distributor',
        'Contractor',
        'WorkorderStatus'
      ];

      if (search.WorkorderStatus === 'Complete' || search.WorkorderStatus === 'Install Complete') {
        return this.matchExact(data.WorkorderStatus, search.WorkorderStatus);
      }

      return columns.every(column => {
        if (
          column === 'WorkorderType' && search.WorkorderType &&
          search.WorkorderStatus && !search.WorkorderID &&
          !search.AccountHolder && !search.Distributor &&
          !search.Contractor
        ) {
          return this.matchExact(data.WorkorderType, search.WorkorderType);
        }

        if (
          column === 'WorkorderStatus' && search.WorkorderType &&
          search.WorkorderStatus && !search.WorkorderID &&
          !search.AccountHolder && !search.Distributor &&
          !search.Contractor
        ) {
          return this.matchExact(data.WorkorderStatus, search.WorkorderStatus);
        }

        return this.matchContains(data[column], search[column]);
      });
    };
  }

  private matchExact(value: any, filter: string): boolean {
    if (!filter) return true;
    return value?.toString().trim().toLowerCase() === filter.toString().trim().toLowerCase();
  }

  private matchContains(value: any, filter: string): boolean {
    if (!filter) return true;
    return value?.toString().trim().toLowerCase().includes(filter.toString().trim().toLowerCase());
  }

  refrehPage() {
    this.router.navigateByUrl('/graphDashboard', { skipLocationChange: false })
      .then(() => this.router.navigate(['/graphDashboard']));
  }

  private getWorkOrders() {
    this.spinner.show();
    const formattedDates = this.defaultDatePicker();
    this.userService.getDashboardBuckets(formattedDates.fromDate, formattedDates.toDate, this.programId).subscribe((data) => {
      if (this.userService.orgCd != null) {
        this.dataSource.data = data["DATA"];

        this.spinner.hide();
        if (this.userService.resourceRole == "ADMIN") {
          this.getMatrix();
        }
      } else {
        this.spinner.hide();
        Swal.fire({
          text: data["Message"],
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    });
  }

  applyFilter(filterValue, value: any = null) {
    if (filterValue?.WorkorderType) {
      this.filteredValues.WorkorderType = filterValue.WorkorderType;
      this.filteredValues.WorkorderStatus = value;
    }
    else if (filterValue) {
      this.filteredValues.WorkorderStatus = filterValue;
      this.filteredValues.WorkorderType = '';
    } else {
      this.globalFilter = filterValue;
    }
    this.dataSource.filter = JSON.stringify(this.filteredValues);
  }

  clearColumn(columnKey: string): void {
    columnKey == 'workorderIDFilter' ? this.workorderIDFilter.reset() :
      columnKey == 'workorderTypeFilter' ? this.workorderTypeFilter.reset() :
        columnKey == 'accountHolderFilter' ? this.accountHolderFilter.reset() :
          columnKey == 'distributorFilter' ? this.distributorFilter.reset() :
            columnKey == 'contractorFilter' ? this.contractorFilter.reset() :
              columnKey == 'workorderStatusFilter' ? this.workorderStatusFilter.reset() : "";
  }

  handleEnrollmentWorkorderClick(data, enrollmentId) {
    const body = { Id: data.WorkorderID };
    this.workorderService.currentWorkorderID = data.WorkorderID;
    this.enrollmentService.currentEnrollmentID = enrollmentId;
    this.router.navigate(['/workorderGetSave', data.Group, data.SubGroup, data.WorkorderID, data?.SubGroup1 ?? "ALL"]);
  }

  processData(data: any[]): void {
    const firstItem = data[0];
    const keys = Object.keys(firstItem).filter(key => key !== 'WorkorderType');
    this.displayedLabels = keys;

    this.barChartData = data.map(item => {
      const values = keys.map(key => item[key]);
      return {
        label: `${item.WorkorderType}`,
        data: values,
      };
    });
  }
}